import {
  getIntersectionRoot,
  getScrollTop,
  scrollTo as pageScrollTo,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class DarkrimBlogPost extends HTMLElement {
  #activeId = '';
  #copyTimer = 0;
  #observer = null;
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  #titles = [];
  #tocLinks = [];

  connectedCallback() {
    this.#titles = Array.from(this.querySelectorAll('[data-darkrim-blog-post-title]'));
    this.#tocLinks = Array.from(this.querySelectorAll('[data-darkrim-blog-post-toc-link]'));

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollContainerChange);

    this.#observeTitles();
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleScrollContainerChange);
    this.#observer?.disconnect();
    this.#observer = null;
    window.clearTimeout(this.#copyTimer);
  }

  #handleClick = (event) => {
    const eventTarget = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!(eventTarget instanceof Element)) return;

    const tocLink = eventTarget.closest('[data-darkrim-blog-post-toc-link]');
    if (tocLink && this.contains(tocLink)) {
      const title = this.#titleByHref(tocLink.getAttribute('href'));
      if (title) {
        event.preventDefault();
        this.#scrollTo(title);
        this.#setActive(title.id, { updateHash: true });
      }
      return;
    }

    const share = eventTarget.closest('[data-darkrim-blog-post-share]');
    if (share && this.contains(share) && share.dataset.shareNetwork === 'copy') {
      event.preventDefault();
      this.#copyLink(share);
    }
  };

  #handleBlockSelect = (event) => {
    const eventTarget = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!(eventTarget instanceof Element)) return;

    const title = eventTarget.closest('[data-darkrim-blog-post-title]');
    if (title && this.contains(title)) {
      this.#scrollTo(title, true);
      this.#setActive(title.id);
    }
  };

  #handleScrollContainerChange = () => {
    this.#observeTitles();
  };

  #observeTitles() {
    this.#observer?.disconnect();
    if (this.#titles.length === 0 || this.#tocLinks.length === 0) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: getIntersectionRoot(),
      rootMargin: '-20% 0px -60% 0px',
      threshold: [0, 1],
    });

    this.#titles.forEach((title) => this.#observer?.observe(title));
    this.#setActive(this.#titles[0]?.id || '');
  }

  #handleIntersect = (entries) => {
    const visible = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

    if (visible[0]?.target?.id) {
      this.#setActive(visible[0].target.id);
    }
  };

  #setActive(id, { updateHash = false } = {}) {
    if (!id || id === this.#activeId) {
      if (updateHash) this.#syncHash(id);
      return;
    }

    this.#activeId = id;

    this.#tocLinks.forEach((link) => {
      const isActive = this.#idFromHref(link.getAttribute('href')) === id;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (updateHash) this.#syncHash(id);
  }

  #syncHash(id) {
    if (!id || !window.history?.replaceState) return;
    window.history.replaceState(null, '', `#${id}`);
  }

  #idFromHref(href) {
    if (!href) return '';
    const hashIndex = href.indexOf('#');
    if (hashIndex === -1) return '';
    try {
      return decodeURIComponent(href.slice(hashIndex + 1));
    } catch (error) {
      return href.slice(hashIndex + 1);
    }
  }

  #titleByHref(href) {
    const id = this.#idFromHref(href);
    if (!id) return null;
    return this.#titles.find((title) => title.id === id) || document.getElementById(id);
  }

  #scrollTo(element, immediate = false) {
    if (!(element instanceof HTMLElement)) return;

    const headerHeight = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const top = getScrollTop() + element.getBoundingClientRect().top - headerHeight - 16;
    const behavior = immediate || this.#reducedMotionQuery.matches ? 'auto' : 'smooth';

    pageScrollTo({ top: Math.max(0, top), behavior });
  }

  async #copyLink(button) {
    const url = button.dataset.shareUrl || window.location.href;
    const live = this.querySelector('[data-darkrim-blog-post-copy-status]');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }

      button.classList.add('is-copied');
      if (live) live.textContent = live.dataset.copiedMessage || 'Link copied';
      window.clearTimeout(this.#copyTimer);
      this.#copyTimer = window.setTimeout(() => {
        button.classList.remove('is-copied');
        if (live) live.textContent = '';
      }, 2000);
    } catch (error) {
      button.classList.remove('is-copied');
    }
  }
}

if (!customElements.get('darkrim-blog-post')) {
  customElements.define('darkrim-blog-post', DarkrimBlogPost);
}
